import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type ColumnDef } from '@tanstack/react-table'
import { Check, X, Send, Plus, Trash2, Loader2, Search, Palmtree, Calendar } from 'lucide-react'
import { toast } from 'sonner'
import { PageHeader } from '@/components/layout/page-header'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { DataTable } from '@/components/ui/data-table'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Combobox } from '@/components/ui/combobox'
import { vacation as vacationApi } from '@/utils/api'
import { useVacationRequests, useVacationSummary, useVacationStats } from '@/hooks/useVacation'
import { useUsers } from '@/hooks/useUsers'
import { getStatusLabel } from '@/utils/formatters'
import { cn } from '@/lib/utils'

interface VacationRequest {
  id: number; user_name: string; start_date: string; end_date: string
  days_requested: number; status: string; type?: string; rejection_reason?: string
}
interface VacationSummary { year: number; employees: { user_id: number; name: string; remaining: number; entitlement: number }[] }
interface UserOption { id: number; name: string }

function AddVacationForm({ users, onClose, onSuccess }: { users: UserOption[]; onClose: () => void; onSuccess: () => void }) {
  const [formData, setFormData] = useState({ user_id: '', start_date: '', end_date: '' })
  const [saving, setSaving] = useState(false)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.user_id || !formData.start_date || !formData.end_date) { toast.error('Bitte alle Felder ausfuellen'); return }
    setSaving(true)
    try { await vacationApi.create({ ...formData, user_id: parseInt(formData.user_id), status: 'approved' }); onSuccess() }
    catch (err) { toast.error(err instanceof Error ? err.message : 'Fehler') }
    finally { setSaving(false) }
  }
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2"><label className="text-sm font-medium">Mitarbeiter</label>
        <Combobox
          options={users.map(u => ({ value: String(u.id), label: u.name }))}
          value={formData.user_id}
          onChange={(v) => setFormData({ ...formData, user_id: v })}
          placeholder="Auswählen..."
          searchPlaceholder="Mitarbeiter suchen..."
          emptyText="Kein Mitarbeiter gefunden"
        />
      </div>
      <div className="space-y-2"><label className="text-sm font-medium">Von</label><Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} required /></div>
      <div className="space-y-2"><label className="text-sm font-medium">Bis</label><Input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} required /></div>
      <div className="flex justify-end gap-3 pt-4">
        <Button variant="outline" type="button" onClick={onClose}>Abbrechen</Button>
        <Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Eintragen</Button>
      </div>
    </form>
  )
}

const statusVariant = (s: string) => s === 'approved' ? 'success' as const : s === 'rejected' ? 'destructive' as const : s === 'pending' ? 'warning' as const : 'secondary' as const

export default function Urlaub() {
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [rejectModal, setRejectModal] = useState<{ open: boolean; request: VacationRequest | null }>({ open: false, request: null })
  const [rejectReason, setRejectReason] = useState('')
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; request: VacationRequest | null }>({ open: false, request: null })
  const [addModal, setAddModal] = useState(false)

  const requestParams = statusFilter === 'all' ? undefined : { status: statusFilter }
  const { data: requests = [], isLoading: loading } = useVacationRequests(requestParams)
  const { data: summary } = useVacationSummary({ year: new Date().getFullYear() })
  const { data: stats } = useVacationStats({ year: new Date().getFullYear(), month: new Date().getMonth() + 1 })
  const { data: users = [] } = useUsers()

  const statCards = [
    { label: 'Aktuell im Urlaub', value: stats?.activeCount || 0, icon: Palmtree, color: 'text-green-600', bg: 'bg-green-50' },
    { label: 'Urlaubstage diesen Monat', value: stats?.monthlyDays || 0, icon: Calendar, color: 'text-yellow-600', bg: 'bg-yellow-50' },
    { label: 'Urlaubstage dieses Jahr', value: stats?.yearlyDays || 0, icon: Calendar, color: 'text-blue-600', bg: 'bg-blue-50' },
  ]

  const invalidateVacation = () => queryClient.invalidateQueries({ queryKey: ['vacation'] })

  const approveMutation = useMutation({
    mutationFn: (id: number) => vacationApi.approve(id),
    onSuccess: () => { toast.success('Genehmigt'); invalidateVacation() },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Fehler'),
  })

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => vacationApi.reject(id, reason),
    onSuccess: () => { toast.success('Abgelehnt'); setRejectModal({ open: false, request: null }); setRejectReason(''); invalidateVacation() },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Fehler'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => vacationApi.delete(id),
    onSuccess: (r) => { toast.success(r.data.restored_days > 0 ? `Gelöscht. ${r.data.restored_days} Tage zurückerstattet.` : 'Gelöscht'); setDeleteModal({ open: false, request: null }); invalidateVacation() },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Fehler'),
  })

  const reminderMutation = useMutation({
    mutationFn: () => vacationApi.sendReminder(),
    onSuccess: (r) => toast.success(r.data.message),
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Fehler'),
  })

  const handleApprove = (id: number) => approveMutation.mutate(id)
  const handleReject = () => {
    if (!rejectReason.trim()) { toast.error('Bitte Ablehnungsgrund angeben'); return }
    rejectMutation.mutate({ id: rejectModal.request!.id, reason: rejectReason })
  }
  const handleDelete = () => { if (!deleteModal.request) return; deleteMutation.mutate(deleteModal.request.id) }
  const handleSendReminder = () => reminderMutation.mutate()

  const processing = approveMutation.isPending || rejectMutation.isPending || deleteMutation.isPending
  const filteredRequests = search
    ? requests.filter((r: VacationRequest) => r.user_name?.toLowerCase().includes(search.toLowerCase()))
    : requests
  const pendingCount = requests.filter((r: VacationRequest) => r.status === 'pending').length

  const columns: ColumnDef<VacationRequest>[] = [
    {
      accessorKey: 'user_name',
      header: 'Mitarbeiter',
      cell: ({ row }) => <span className="font-medium">{row.original.user_name}</span>,
    },
    {
      accessorKey: 'start_date',
      header: 'Zeitraum',
      cell: ({ row }) => `${row.original.start_date} - ${row.original.end_date}`,
    },
    {
      accessorKey: 'days_requested',
      header: 'Tage',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <div>
          <Badge variant={statusVariant(row.original.status)}>{getStatusLabel(row.original.status)}</Badge>
          {row.original.rejection_reason && (
            <p className="mt-1 text-xs text-destructive">Grund: {row.original.rejection_reason}</p>
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Aktionen',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex gap-1">
          {row.original.status === 'pending' && <>
            <Button size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); handleApprove(row.original.id) }} disabled={processing}><Check className="h-4 w-4" /></Button>
            <Button variant="destructive" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); setRejectModal({ open: true, request: row.original }) }}><X className="h-4 w-4" /></Button>
          </>}
          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteModal({ open: true, request: row.original }) }}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ),
    },
  ]

  return (
    <div>
      <PageHeader title="Urlaubsverwaltung" description="Verwalten Sie Urlaubsantraege und Urlaubstage" actions={
        <div className="flex gap-2">
          <Button variant="outline" className="bg-white" onClick={handleSendReminder}><Send className="h-4 w-4" /> Erinnerung senden</Button>
          <Button variant="outline" className="bg-white" onClick={() => setAddModal(true)}><Plus className="h-4 w-4" /> Urlaub eintragen</Button>
        </div>
      } />

      <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-3">
        {statCards.map((s, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div><p className="text-3xl font-bold">{s.value}</p><p className="mt-1 text-sm text-muted-foreground">{s.label}</p></div>
                <div className={cn('rounded-lg p-3', s.bg)}><s.icon className={cn('h-6 w-6', s.color)} /></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {pendingCount > 0 && (
            <Card className="border-l-4 border-l-accent-500">
              <CardHeader><CardTitle>Offene Antraege ({pendingCount})</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {requests.filter((r: VacationRequest) => r.status === 'pending').map(req => (
                  <div key={req.id} className="flex items-center justify-between rounded-lg bg-muted p-4">
                    <div>
                      <p className="font-medium">{req.user_name}</p>
                      <p className="text-sm text-muted-foreground">{req.start_date} - {req.end_date} <span className="ml-2">({req.days_requested} Tage)</span></p>
                      {req.type && <p className="text-xs text-muted-foreground">Typ: {req.type === 'unpaid_vacation' ? 'Unbezahlt' : 'Bezahlt'}</p>}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleApprove(req.id)} disabled={processing}><Check className="h-4 w-4" /> Genehmigen</Button>
                      <Button variant="destructive" size="sm" onClick={() => setRejectModal({ open: true, request: req })}><X className="h-4 w-4" /> Ablehnen</Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          <Card className="mb-6"><CardContent className="flex flex-wrap gap-4 py-4">
            <div className="relative min-w-[200px] max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Suche..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="flex h-9 w-40 rounded-md border border-input bg-white px-3 text-sm">
              <option value="all">Alle</option><option value="pending">Ausstehend</option><option value="approved">Genehmigt</option><option value="rejected">Abgelehnt</option>
            </select>
          </CardContent></Card>

          {loading ? <div className="space-y-2">{Array.from({length:4}).map((_,i) => <Skeleton key={i} className="h-12 w-full" />)}</div> : (
            <DataTable columns={columns} data={filteredRequests} emptyText="Keine Urlaubsantraege" />
          )}
        </div>

        {/* Summary */}
        <Card>
          <CardHeader><CardTitle>Urlaubsuebersicht {summary?.year}</CardTitle></CardHeader>
          <CardContent className="max-h-[600px] overflow-y-auto p-0">
            <table className="w-full">
              <thead><tr className="border-b bg-muted/50"><th className="px-4 py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Name</th><th className="px-4 py-2 text-right text-xs font-semibold uppercase text-muted-foreground">Rest</th></tr></thead>
              <tbody>{summary?.employees?.map(emp => (
                <tr key={emp.user_id} className="border-b last:border-0 hover:bg-muted/50">
                  <td className="px-4 py-2 text-sm font-medium">{emp.name}</td>
                  <td className="px-4 py-2 text-right text-sm"><span className={cn(emp.remaining <= 5 && 'font-medium text-destructive')}>{emp.remaining}</span><span className="text-muted-foreground">/{emp.entitlement}</span></td>
                </tr>
              ))}</tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {/* Reject Dialog */}
      <Dialog open={rejectModal.open} onOpenChange={(o) => !o && setRejectModal({ open: false, request: null })}>
        <DialogContent><DialogHeader><DialogTitle>Urlaubsantrag ablehnen</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Antrag von <strong>{rejectModal.request?.user_name}</strong> ablehnen.</p>
          <div className="space-y-2"><label className="text-sm font-medium">Ablehnungsgrund</label><Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Grund angeben..." required /></div>
          <DialogFooter><Button variant="outline" onClick={() => setRejectModal({ open: false, request: null })}>Abbrechen</Button><Button variant="destructive" onClick={handleReject} disabled={processing}>{processing && <Loader2 className="h-4 w-4 animate-spin" />} Ablehnen</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteModal.open} onOpenChange={(o) => !o && setDeleteModal({ open: false, request: null })}>
        <DialogContent><DialogHeader><DialogTitle>Urlaubsantrag löschen</DialogTitle></DialogHeader>
          {deleteModal.request?.status === 'approved' ? (
            <div className="space-y-3"><p className="font-medium text-orange-600">Dieser Urlaub wurde bereits genehmigt!</p><p className="text-sm text-muted-foreground">{deleteModal.request.start_date} - {deleteModal.request.end_date}. Die {deleteModal.request.days_requested} Tage werden zurückerstattet.</p></div>
          ) : <p className="text-sm text-muted-foreground">Antrag von <strong>{deleteModal.request?.user_name}</strong> löschen?</p>}
          <DialogFooter><Button variant="outline" onClick={() => setDeleteModal({ open: false, request: null })}>Abbrechen</Button><Button variant="destructive" onClick={handleDelete} disabled={processing}>{processing && <Loader2 className="h-4 w-4 animate-spin" />} Löschen</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={addModal} onOpenChange={setAddModal}>
        <DialogContent><DialogHeader><DialogTitle>Urlaub manuell eintragen</DialogTitle></DialogHeader>
          <AddVacationForm users={users} onClose={() => setAddModal(false)} onSuccess={() => { setAddModal(false); invalidateVacation(); toast.success('Urlaub eingetragen') }} />
        </DialogContent>
      </Dialog>
    </div>
  )
}
